import { useCallback, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Action, ResourceType } from '../../../../typings/permissions';
import { window } from '../../../globals';
import { getCookie, setCookie } from '../../../helpers/cookies';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import DismissibleCard from '../../generic/Card/DismissibleCard';
import Link from '../../generic/Link/Link';

import { useContributorLimit } from './hooks/useContributorLimit';

export const CONTRIBUTOR_LIMIT_BANNER_DISMISSED_COOKIE_NAME = 'dismissedContributorLimitWarningBanner';
export const CONTRIBUTOR_LIMIT_BANNER_DISMISSED_COOKIE_EXPIRATION_DAYS = 14;

/**
 * Hook to check if the banner has been dismissed.
 */
const useHasBannerBeenDismissed = () => {
  //  Set the initial state of the banner to the value of the cookie
  const [isDismissed, setIsDismissed] = useState(() =>
    getCookie(CONTRIBUTOR_LIMIT_BANNER_DISMISSED_COOKIE_NAME) === 'true');

  //  Sets the cookie and updates the state to reflect that the banner has been dismissed
  const onDismissBanner = useCallback(() => setIsDismissed(() => {
    setCookie(
      CONTRIBUTOR_LIMIT_BANNER_DISMISSED_COOKIE_NAME,
      'true',
      CONTRIBUTOR_LIMIT_BANNER_DISMISSED_COOKIE_EXPIRATION_DAYS,
    );
    //  Now that we have set the cookie, we can update the state to reflect that the banner has been dismissed
    return true;
  }), []);

  return {
    isDismissed,
    onDismissBanner,
  };
};

type Props = {
  onDismissBanner: () => void;
};

/**
 * Banner to display when the organization has exceeded the contributor limit.
 */
const ContributorCountLimitWarningBanner: VoidFunctionComponent<Props> = ({ onDismissBanner }) => {
  const hasPermission = useHasPermission();
  const { isOverContributorLimit, isContributorsUnlimited, isLoading, value } = useContributorLimit();
  const shouldShowContributorWarningBanner = !isContributorsUnlimited && isOverContributorLimit;
  if (!shouldShowContributorWarningBanner || isLoading) {
    return null;
  }

  const currentUsage = value?.usage || 0;
  const maxLimit = value?.max || 0;
  const canEditBilling = !window.on_prem && hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.OrganizationBillingSettings,
  });

  if (currentUsage < 1 || maxLimit < 1) {
    return null;
  }
  return (
    <DismissibleCard isFullWidth variant="fill" color="pink" onHide={onDismissBanner} padding="medium">
      <DismissibleCard.Header>You have exceeded the maximum number of contributors.</DismissibleCard.Header>
      <DismissibleCard.Content>
        Your contributor count is at{' '}
        <span className="bold">
          {currentUsage}/{maxLimit} contributors.{' '}
        </span>
        FOSSA will continue to work without interruption.{' '}
        {canEditBilling && (
          <>
            Our team will reach out to you soon, or you can increase your contributor limit by{' '}
            <Link to="/account/settings/billing">upgrading your account.</Link>
          </>
        )}
      </DismissibleCard.Content>
    </DismissibleCard>
  );
};

/**
 * The container is here to check if the user has already dismissed the banner, and only render it if it hasn't been dismissed.
 */
const ContributorCountLimitWarningBannerContainer = () => {
  const { isDismissed, onDismissBanner } = useHasBannerBeenDismissed();
  if (isDismissed) {
    return null;
  }
  return <ContributorCountLimitWarningBanner onDismissBanner={onDismissBanner} />;
};

export default ContributorCountLimitWarningBannerContainer;
